/**
 * AI 服务工具函数
 */

import JSZip from 'jszip';

// 动态 require，绕过类型检查
let Document: any, Packer: any, Paragraph: any, TextRun: any, HeadingLevel: any, Table: any, TableCell: any, TableRow: any, WidthType: any, BorderStyle: any;
try {
  const m = require('docx');
  Document = m.Document;
  Packer = m.Packer;
  Paragraph = m.Paragraph;
  TextRun = m.TextRun;
  HeadingLevel = m.HeadingLevel;
  Table = m.Table;
  TableCell = m.TableCell;
  TableRow = m.TableRow;
  WidthType = m.WidthType;
  BorderStyle = m.BorderStyle;
} catch {}

/**
 * 从 docx Blob 中提取纯文本
 */
export async function extractTextFromDocxBlob(blob: Blob): Promise<string> {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    let text = '';
    const docXml = zip.file('word/document.xml');
    if (docXml) {
      const xml = await docXml.async('text');
      text += extractTextFromXml(xml);
    }
    return text;
  } catch (e) {
    console.error('提取docx文本失败:', e);
    return '';
  }
}

function extractTextFromXml(xml: string): string {
  const paragraphs: string[] = [];
  // 按 Word 段落 <w:p> 分割，保留段落结构
  const pRegex = /<w:p[\s>][\s\S]*?<\/w:p>/g;
  let pMatch: RegExpExecArray | null;

  while ((pMatch = pRegex.exec(xml)) !== null) {
    const pXml = pMatch[0];
    const texts: string[] = [];
    const tRegex = /<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
    let tMatch: RegExpExecArray | null;
    while ((tMatch = tRegex.exec(pXml)) !== null) {
      if (tMatch[1]) texts.push(tMatch[1]);
    }
    if (texts.length > 0) {
      paragraphs.push(texts.join(''));
    }
  }

  return paragraphs.join('\n');
}

/**
 * AI法审结果类型
 */
export interface AILegalReviewResult {
  overallScore: number;
  summary: string;
  risks: {
    level: '高' | '中' | '低';
    title: string;
    description: string;
    legalBasis: string;
    suggestion: string;
    context: string;
  }[];
  missingClauses: string[];
  recommendations: string[];
  provider: string;
}

/**
 * AI 生成合同请求参数
 */
export interface AIGenerateContractParams {
  templateName: string;
  templateCategory: string;
  partyA: Record<string, string>;
  partyB: Record<string, string>;
  terms: {
    paymentMethod: string;
    paymentDetails: string;
    validityPeriod: string;
    specialClauses: string;
    purchaseItems: Record<string, string>[];
    totalAmount: string;
    totalAmountUppercase: string;
    installments?: { period: number; percentage: string; amount: string }[];
  };
  employeeName: string;
  contractNumber: string;
  signingDate: string;
}

/**
 * AI 生成合同结果
 */
export interface AIGenerateContractResult {
  contractMarkdown: string;
  provider: string;
  error?: string;
}

/**
 * 执行AI法审
 */
export async function performLegalReview(
  contractText: string
): Promise<AILegalReviewResult> {
  const response = await fetch('/api/legal-review', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contractText }),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`AI法审请求失败 ${response.status}: ${errText}`);
  }

  const result = await response.json();
  return result as AILegalReviewResult;
}

/**
 * 调用 AI 生成合同
 */
export async function generateContractWithAI(
  params: AIGenerateContractParams,
  onProgress?: (stage: string, message: string) => void,
): Promise<AIGenerateContractResult> {
  if (onProgress) onProgress('generating', 'AI 正在根据模板和填写信息生成合同...');

  const response = await fetch('/api/generate-contract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(`AI生成合同请求失败 ${response.status}: ${errText}`);
  }

  const result = await response.json();
  return result as AIGenerateContractResult;
}

// ==================== Markdown 转 Word ====================

/**
 * 将 Markdown 格式合同转换为 Word Blob
 * 处理：标题、粗体、表格、列表等
 */
export async function convertMarkdownToDocx(
  markdown: string,
  title: string
): Promise<Blob> {
  // 先清理 Markdown 标记
  const cleanText = cleanMarkdown(markdown);

  if (Document && Packer && Paragraph && TextRun) {
    try {
      const children = parseMarkdownToDocx(cleanText);
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
            },
          },
          children,
        }],
      });
      const buffer = await Packer.toBuffer(doc);
      const arrayBuf = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
      return new Blob([arrayBuf], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    } catch (e) {
      console.warn('docx 库生成失败，使用降级方案', e);
    }
  }

  // 降级方案
  return generateMinimalDocx(cleanText, title);
}

/**
 * 清理 Markdown 标记，转为纯文本（保留结构）
 * 重要：保留表格的 | 分隔符，以便后续 parseMarkdownToDocx 能识别表格
 */
function cleanMarkdown(md: string): string {
  return md
    // 去掉 ** 粗体标记（但保留表格中的 |）
    .replace(/\*\*(.+?)\*\*/g, '$1')
    // 去掉 * 斜体标记
    .replace(/\*(.+?)\*/g, '$1')
    // 去掉 ` 代码标记
    .replace(/`(.+?)`/g, '$1')
    // 去掉分隔线 ---（但保留表格中的 --- 分隔行）
    .replace(/^(?![^|]*\|)[-]{3,}$/gm, '')
    // 去掉标题标记 #（保留文字）
    .replace(/^#+\s+/gm, '')
    // 去掉 AI 可能输出的 <think> 思考标签
    .replace(/<think>[\s\S]*?<\/think>/g, '')
    // 去掉可能的解释性前言（如"以下是生成的合同..."）
    .replace(/^(以下是|根据您的要求|生成合同如下|合同内容如下)[：:].*$/gm, '')
    .trim();
}

/**
 * 解析清理后的文本，生成 docx 节点
 */
function parseMarkdownToDocx(text: string): any[] {
  const lines = text.split('\n');
  const children: any[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // 表格检测：包含 | 的行（支持有/无空格两种格式）
    // 排除：条款标题行（第一条、第二条...）
    const pipeCount = (line.match(/\|/g) || []).length;
    const isTableRow = pipeCount >= 2 && !line.match(/^第[一二三四五六七八九十\d]+条/);
    const isSeparatorRow = /^\s*\|?[\s\-:]+\|?[\s\-:]*\|?$/.test(line);

    if (isTableRow && !isSeparatorRow) {
      // 收集表格所有行（包括分隔行）
      const tableLines: string[] = [line];
      let j = i + 1;
      while (j < lines.length) {
        const nextLine = lines[j].trim();
        if (!nextLine || !nextLine.includes('|')) break;
        tableLines.push(nextLine);
        j++;
      }
      i = j - 1;

      const tableNode = buildTable(tableLines);
      if (tableNode) children.push(tableNode);
      continue;
    }

    // 标题行
    if (line.match(/^第[一二三四五六七八九十\d]+条/)) {
      children.push(new Paragraph({
        children: [new TextRun({ text: line, bold: true, size: 24 })],
        spacing: { before: 200, after: 100 },
      }));
      continue;
    }

    // 普通段落
    const runs = parseInlineFormatting(line);
    children.push(new Paragraph({
      children: runs,
      spacing: { after: 120 },
    }));
  }

  return children;
}

/**
 * 解析行内格式（粗体等）
 */
function parseInlineFormatting(line: string): any[] {
  const runs: any[] = [];
  // 处理 **粗体** 和 *斜体*
  const parts = line.split(/(\*\*.*?\*\*|\*.*?\*)/g);
  for (const part of parts) {
    if (part.startsWith('**') && part.endsWith('**')) {
      runs.push(new TextRun({ text: part.slice(2, -2), bold: true }));
    } else if (part.startsWith('*') && part.endsWith('*')) {
      runs.push(new TextRun({ text: part.slice(1, -1), italics: true }));
    } else {
      runs.push(new TextRun({ text: part }));
    }
  }
  return runs;
}

/**
 * 构建 Word 表格
 * 支持两种 Markdown 表格格式：
 * 1. | 内容1 | 内容2 | （有空格）
 * 2. |内容1|内容2| （无空格）
 */
function buildTable(lines: string[]): any | null {
  if (!Table || !TableCell || !TableRow) return null;

  // 过滤掉表头分隔行（如 | --- | --- |）
  const dataLines = lines.filter(l => !l.match(/^\s*\|?[\s\-:]+\|[\s\-:]*\|?$/));
  if (dataLines.length === 0) return null;

  const rows: any[] = [];
  let maxCols = 0;

  for (const line of dataLines) {
    // 去掉首尾的 |，然后按 | 分割单元格
    let cellStr = line.trim();
    if (cellStr.startsWith('|')) cellStr = cellStr.slice(1);
    if (cellStr.endsWith('|')) cellStr = cellStr.slice(0, -1);
    
    const cells = cellStr.split('|').map(c => c.trim());
    if (cells.length === 0) continue;
    
    maxCols = Math.max(maxCols, cells.length);
    
    const tableCells = cells.map(cell =>
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: cell || ' ' })] })],
        width: { size: Math.floor(100 / cells.length), type: WidthType.PERCENTAGE },
      })
    );
    rows.push(new TableRow({ children: tableCells }));
  }

  if (rows.length === 0) return null;

  // 计算每列宽度
  const colWidths = Array(maxCols).fill(Math.floor(100 / maxCols));

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: colWidths,
  });
}

/**
 * 降级方案：手动拼装最小 WordprocessingML .docx
 */
function generateMinimalDocx(text: string, title: string): Promise<Blob> {
  const lines = text.split('\n').filter(l => l.trim());
  const paragraphs: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let xmlText = escapeXml(trimmed);

    // 去掉残留的 ** 标记
    xmlText = xmlText.replace(/\*\*/g, '');

    // 标题
    if (trimmed.match(/^第[一二三四五六七八九十\d]+条/)) {
      paragraphs.push(`<w:p>
        <w:pPr><w:jc w:val="left"/></w:pPr>
        <w:r><w:rPr><w:sz w:val="24"/><w:szCs w:val="24"/><w:b/></w:rPr><w:t xml:space="preserve">${xmlText}</w:t></w:r>
      </w:p>`);
    } else {
      paragraphs.push(`<w:p><w:r><w:t xml:space="preserve">${xmlText}</w:t></w:r></w:p>`);
    }
  }

  const body = paragraphs.join('\n');
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${body}
    <w:sectPr><w:pgSz w:w="11906" w:h="16838"/></w:sectPr>
  </w:body>
</w:document>`;

  const zip = new JSZip();
  zip.file('[Content_Types].xml', getContentTypesXml());
  zip.file('_rels/.rels', getRelsXml());
  zip.file('word/_rels/document.xml.rels', getDocumentRelsXml());
  zip.file('word/document.xml', documentXml);

  return zip.generateAsync({
    type: 'arraybuffer',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  }).then((arrayBuf: ArrayBuffer) => {
    return new Blob([arrayBuf], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
  });
}

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function getContentTypesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`;
}

function getRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;
}

function getDocumentRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
</Relationships>`;
}
